import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/hooks/useApiClient';
import { useToast } from '@/contexts/ToastContext';
import { LoadingSpinner, PageError } from '@/components/ui';
import type { Equipment, EquipmentSector, EquipmentTodayStatus } from '@haccp/api-client';
import { formatTemperature } from '@/utils/temperature';
import { getSectorIcon, getSectorColor, getEquipmentTypeIcon, EQUIPMENT_TYPES } from '@/utils/equipment';
import { useTranslation } from 'react-i18next';

export default function EquipmentPage() {
  const apiClient = useApiClient();
  const { t } = useTranslation();

  const REFRESH_INTERVAL = 30 * 1000;

  const { data: equipmentList = [], isLoading: eqLoading, error: eqError } = useQuery({
    queryKey: ['equipment'],
    queryFn: () => apiClient.getEquipment(),
    refetchInterval: REFRESH_INTERVAL,
  });

  const { data: sectors = [], isLoading: secLoading, error: secError } = useQuery({
    queryKey: ['equipment-sectors'],
    queryFn: () => apiClient.getEquipmentSectors(),
    refetchInterval: REFRESH_INTERVAL,
  });

  const { data: todayStatus = [], isLoading: statusLoading, error: statusError } = useQuery({
    queryKey: ['equipment-today-status'],
    queryFn: () => apiClient.getEquipmentTodayStatus(),
    refetchInterval: REFRESH_INTERVAL,
  });

  const { data: oilStationsRaw } = useQuery({
    queryKey: ['oil-stations'],
    queryFn: async () => {
      const result = await apiClient.getOilStations();
      const stations = result?.stations || result?.data?.stations || result || [];
      return Array.isArray(stations) ? stations : [];
    },
    refetchInterval: REFRESH_INTERVAL,
  });
  const oilStations = Array.isArray(oilStationsRaw) ? oilStationsRaw : [];

  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ name: '', type: '', minTempC: 0, maxTempC: 0 });

  const updateMut = useMutation({
    mutationFn: (data: { id: string; name: string; type: string; minTempC: number; maxTempC: number }) =>
      apiClient.updateEquipment(data.id, { name: data.name, type: data.type, minTempC: data.minTempC, maxTempC: data.maxTempC }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment'] });
      queryClient.invalidateQueries({ queryKey: ['equipment-today-status'] });
      setEditingId(null);
      addToast(t('equipment.updated'), 'success');
    },
    onError: () => addToast(t('equipment.updateError'), 'error'),
  });

  const startEdit = (eq: Equipment) => {
    setEditingId(eq.id);
    setEditForm({ name: eq.name, type: eq.type, minTempC: eq.minTempC, maxTempC: eq.maxTempC });
  };

  const statusMap = useMemo(() => {
    const m = new Map<string, EquipmentTodayStatus>();
    for (const s of todayStatus) m.set(s.equipmentId, s);
    return m;
  }, [todayStatus]);

  const grouped = useMemo(() => {
    const bySector = new Map<string, Equipment[]>();
    const unassigned: Equipment[] = [];

    for (const eq of equipmentList) {
      if (eq.sectorId) {
        const list = bySector.get(eq.sectorId) || [];
        list.push(eq);
        bySector.set(eq.sectorId, list);
      } else {
        unassigned.push(eq);
      }
    }

    return { bySector, unassigned };
  }, [equipmentList]);

  const isLoading = eqLoading || secLoading || statusLoading;
  const error = eqError || secError || statusError;
  const totalEquipment = equipmentList.length + oilStations.length;

  const pendingCount = useMemo(() => {
    let tempPending = 0;
    let cleanPending = 0;
    for (const eq of equipmentList) {
      const st = statusMap.get(eq.id);
      if (st && !st.hasTemperatureToday) tempPending++;
      if (st && st.cleaningTotal > 0 && st.cleaningDone < st.cleaningTotal) cleanPending++;
    }
    return { tempPending, cleanPending };
  }, [equipmentList, statusMap]);

  const getTypeIcon = (type: string) => getEquipmentTypeIcon(type);

  const renderStatusBadges = (eq: Equipment) => {
    const st = statusMap.get(eq.id);
    if (!st) return null;

    return (
      <div className="flex flex-wrap gap-1.5 mt-1.5">
        {st.hasTemperatureToday ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
            ✓ {t('equipment.tempOk')}
          </span>
        ) : (
          <a
            href={`/temperature-logs?equipment=${eq.id}`}
            className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700 hover:bg-red-200 transition"
          >
            ● {t('equipment.tempPendingBadge')}
          </a>
        )}
        {st.cleaningTotal > 0 && (
          st.cleaningDone >= st.cleaningTotal ? (
            <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
              ✓ {t('equipment.cleanOk')}
            </span>
          ) : (
            <a
              href="/cleaning"
              className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 hover:bg-orange-200 transition"
            >
              ● {t('equipment.cleanPendingBadge', { done: st.cleaningDone, total: st.cleaningTotal })}
            </a>
          )
        )}
      </div>
    );
  };

  const renderEquipmentRow = (eq: Equipment) => {
    if (editingId === eq.id) {
      return (
        <div key={eq.id} className="p-4 bg-blue-50 rounded-lg border-2 border-blue-300">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('equipment.name')}</label>
              <input
                type="text"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('equipment.type')}</label>
              <select
                value={editForm.type}
                onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
              >
                {EQUIPMENT_TYPES.map((tp) => (
                  <option key={tp} value={tp}>{tp}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Min °C</label>
              <input
                type="number"
                step="0.1"
                value={editForm.minTempC}
                onChange={(e) => setEditForm({ ...editForm, minTempC: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Max °C</label>
              <input
                type="number"
                step="0.1"
                value={editForm.maxTempC}
                onChange={(e) => setEditForm({ ...editForm, maxTempC: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button
              onClick={() => setEditingId(null)}
              className="px-4 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              {t('equipment.cancel')}
            </button>
            <button
              onClick={() => updateMut.mutate({ id: eq.id, ...editForm })}
              disabled={updateMut.isPending || !editForm.name.trim()}
              className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
            >
              {updateMut.isPending ? t('equipment.saving') : t('equipment.save')}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div key={eq.id} className="flex items-start justify-between p-4 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-sm transition">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <span className="text-xl mt-0.5">{getTypeIcon(eq.type)}</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900">{eq.name}</span>
              <span className="text-xs text-gray-500">{eq.type}</span>
              {eq.minTempC !== undefined && eq.maxTempC !== undefined && (
                <span className="text-xs text-blue-600 font-medium">
                  {formatTemperature(eq.minTempC)} — {formatTemperature(eq.maxTempC)}
                </span>
              )}
            </div>
            {renderStatusBadges(eq)}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0 ml-2">
          <button
            onClick={() => startEdit(eq)}
            className="text-sm text-gray-500 hover:text-blue-600 font-medium px-2 py-1.5 rounded-lg hover:bg-blue-50 transition"
            title={t('common.edit')}
          >
            ✏️
          </button>
          <a
            href={`/temperature-logs?equipment=${eq.id}`}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium px-3 py-1.5 rounded-lg hover:bg-blue-50 transition"
          >
            {t('equipment.readings')}
          </a>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gray-100 rounded-lg">
              <span className="text-2xl">🏗️</span>
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('equipment.title')}</h1>
              <p className="text-gray-600 mt-1">{t('equipment.subtitle', { count: totalEquipment })}</p>
            </div>
          </div>
          {!isLoading && (pendingCount.tempPending > 0 || pendingCount.cleanPending > 0) && (
            <div className="flex gap-3 mt-4">
              {pendingCount.tempPending > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg">
                  <span className="text-red-600 text-sm font-semibold">{t('equipment.tempPendingAlert', { count: pendingCount.tempPending })}</span>
                </div>
              )}
              {pendingCount.cleanPending > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-lg">
                  <span className="text-orange-600 text-sm font-semibold">{t('equipment.cleanPendingAlert', { count: pendingCount.cleanPending })}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isLoading ? (
          <LoadingSpinner message={t('equipment.loading')} />
        ) : error ? (
          <PageError message={t('equipment.error')} />
        ) : (
          <div className="space-y-6">
            {sectors.map((sector: EquipmentSector) => {
              const sectorEquipment = grouped.bySector.get(sector.id) || [];
              if (sectorEquipment.length === 0) return null;
              const icon = getSectorIcon(sector.name);
              const color = getSectorColor(sector.name);

              return (
                <div key={sector.id} className="space-y-2">
                  <div className={`flex items-center gap-3 px-4 py-3 rounded-lg bg-white border border-gray-200 border-l-4 ${color.border}`}>
                    <span className="text-xl">{icon}</span>
                    <h2 className="text-lg font-semibold text-gray-900">{sector.name}</h2>
                    <span className="text-sm font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                      {sectorEquipment.length}
                    </span>
                  </div>
                  <div className="space-y-2 pl-2">
                    {sectorEquipment.map((eq) => renderEquipmentRow(eq))}
                  </div>
                </div>
              );
            })}

            {oilStations.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white border border-gray-200 border-l-4 border-l-yellow-400">
                  <span className="text-xl">🍟</span>
                  <h2 className="text-lg font-semibold text-gray-900">{t('equipment.fryers')}</h2>
                  <span className="text-sm font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                    {oilStations.length}
                  </span>
                </div>
                <div className="space-y-2 pl-2">
                  {oilStations.map((station: any) => (
                    <div key={station.id} className="flex items-start justify-between p-4 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-sm transition">
                      <div className="flex items-start gap-3">
                        <span className="text-xl mt-0.5">🍟</span>
                        <div>
                          <span className="font-semibold text-gray-900">{station.name}</span>
                          <div className="flex gap-1.5 mt-1.5">
                            {station.lastCheck?.quality && (
                              <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                                station.lastCheck.quality === 'GOOD' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              }`}>
                                {station.lastCheck.quality === 'GOOD' ? `✓ ${t('equipment.oilGood')}` : `● ${t('equipment.oilBad')}`}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <a
                        href="/oil"
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium px-3 py-1.5 rounded-lg hover:bg-blue-50 transition shrink-0 ml-2"
                      >
                        {t('equipment.controls')}
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {grouped.unassigned.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white border border-gray-200 border-l-4 border-l-gray-400">
                  <span className="text-xl">📋</span>
                  <h2 className="text-lg font-semibold text-gray-900">{t('equipment.unassigned')}</h2>
                  <span className="text-sm font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                    {grouped.unassigned.length}
                  </span>
                </div>
                <div className="space-y-2 pl-2">
                  {grouped.unassigned.map((eq) => renderEquipmentRow(eq))}
                </div>
              </div>
            )}

            {totalEquipment === 0 && (
              <div className="bg-white rounded-xl shadow p-8 text-center">
                <span className="text-5xl block mb-4">🏗️</span>
                <h2 className="text-xl font-bold text-gray-900 mb-2">{t('equipment.noEquipmentTitle')}</h2>
                <p className="text-gray-500">{t('equipment.noEquipmentDesc')}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
